import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, Settings2 } from 'lucide-react';

import {
  useArchiveNotification,
  useMarkAllRead,
  useMarkRead,
  useNotificationInbox,
  useNotificationStream,
  useUnreadCount,
} from '@frontend/api/hooks/notifications';
import { cn } from '@frontend/lib/cn';
import { formatRelative } from '@frontend/lib/format';
import { IconButton, Button } from '@frontend/components/ui/Button';
import { Drawer } from '@frontend/components/ui/Modal';
import { EmptyState, LoadingRows } from '@frontend/components/ui/States';

export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const streamStatus = useNotificationStream(true);
  // Poll only when the live stream has given up. Otherwise the stream is authoritative.
  const unread = useUnreadCount(streamStatus === 'OFFLINE');
  const count = unread.data?.count ?? 0;

  return (
    <>
      <div className="relative">
        <IconButton
          variant="ghost"
          size="sm"
          label={count === 0 ? 'Notifications' : `Notifications, ${count} unread`}
          icon={streamStatus === 'OFFLINE' ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          onClick={() => setOpen(true)}
          data-testid="notification-bell"
        />
        {count > 0 && (
          <span
            aria-hidden
            className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-error px-1 text-body font-semibold leading-none text-content-inverse"
            data-testid="unread-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </div>
      <NotificationPanel open={open} onClose={() => setOpen(false)} streamStatus={streamStatus} />
    </>
  );
}

function NotificationPanel({
  open,
  onClose,
  streamStatus,
}: {
  open: boolean;
  onClose: () => void;
  streamStatus: string;
}): JSX.Element {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const inbox = useNotificationInbox({ unreadOnly, limit: 25 });
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const archive = useArchiveNotification();

  return (
    <Drawer open={open} onClose={onClose} title="Notifications" data-testid="notification-panel">
      <div className="flex items-center gap-1 border-b border-line px-3 py-2">
        <button
          type="button"
          onClick={() => setUnreadOnly(false)}
          className={cn(
            'rounded-sm px-2 py-1 text-body',
            unreadOnly ? 'text-content-secondary hover:text-content' : 'bg-surface-selected font-medium text-content',
          )}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setUnreadOnly(true)}
          className={cn(
            'rounded-sm px-2 py-1 text-body',
            unreadOnly ? 'bg-surface-selected font-medium text-content' : 'text-content-secondary hover:text-content',
          )}
        >
          Unread
        </button>
        <Button
          variant="ghost"
          size="xs"
          className="ml-auto"
          leadingIcon={<CheckCheck className="h-3 w-3" />}
          onClick={() => markAllRead.mutate()}
          isLoading={markAllRead.isPending}
        >
          Mark all read
        </Button>
      </div>

      {streamStatus === 'OFFLINE' && (
        <p className="border-b border-warning-border bg-warning-bg px-3 py-2 text-body text-content">
          Live updates are not connected. This list refreshes every minute instead — reload the page to
          reconnect.
        </p>
      )}

      {inbox.isLoading ? (
        <LoadingRows rows={5} columns={1} />
      ) : (inbox.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title={unreadOnly ? 'Nothing unread' : 'No notifications yet'}
          description={
            unreadOnly
              ? 'You are up to date. Switch to "All" to see notifications you have already read.'
              : 'Approvals, timesheet reminders and payroll updates will appear here. You can choose which events notify you, and how, in notification settings.'
          }
          icon={<Bell aria-hidden className="h-7 w-7" />}
        />
      ) : (
        <ul className="divide-y divide-line-subtle">
          {(inbox.data?.items ?? []).map((notification) => (
            <li
              key={notification.id}
              className={cn('px-3 py-2.5', !notification.isRead && 'bg-surface-selected/40')}
              data-testid={`notification-${notification.id}`}
            >
              <div className="flex items-start gap-2">
                {!notification.isRead && (
                  <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-interactive" />
                )}
                <div className={cn('min-w-0 flex-1', notification.isRead && 'pl-3.5')}>
                  <p className="text-row font-medium text-content">{notification.title}</p>
                  <p className="truncate-2 mt-0.5 text-body text-content-secondary">{notification.body}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-body text-content-tertiary">
                      {formatRelative(notification.createdAt)}
                    </span>
                    {notification.actionUrl !== null && (
                      <Link
                        to={notification.actionUrl}
                        onClick={() => {
                          if (!notification.isRead) markRead.mutate(notification.id);
                        }}
                        className="text-body font-medium text-content-link"
                      >
                        {notification.actionLabel ?? 'Open'}
                      </Link>
                    )}
                    {!notification.isRead && (
                      <button
                        type="button"
                        onClick={() => markRead.mutate(notification.id)}
                        className="text-body text-content-secondary hover:text-content"
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => archive.mutate(notification.id)}
                      className="text-body text-content-secondary hover:text-content"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-line px-3 py-2">
        <Link
          to="/notifications/preferences"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-body text-content-link"
        >
          <Settings2 aria-hidden className="h-3 w-3" />
          Notification settings
        </Link>
      </div>
    </Drawer>
  );
}
